/**
 * loadingMap / errorMap keys (and cache keys) for the catalogue import slice.
 *
 * In their own module, with no imports of their own, purely to keep
 * CatalogImportDb and CatalogImportThunk out of a circular import: the slice
 * needs the thunks at module-evaluation time, and components / hooks need these
 * keys. Same reason as ImageAnalyzeKeys.ts. CatalogImportDb re-exports them.
 *
 * Per-row operations key by id so acting on one import / draft never disables
 * the rest of a list.
 */

export const CATALOG_IMPORT_CONFIG_KEY = "fetchCatalogImportConfig";
export const CATALOG_IMPORT_LIST_KEY = "fetchCatalogImports";
export const CATALOG_IMPORT_MORE_KEY = "fetchMoreCatalogImports";
/** Create + upload-url + start of the upload flow share one key: one upload runs at a time. */
export const CATALOG_IMPORT_CREATE_KEY = "createCatalogImport";

export const catalogImportDetailKey = (importId: string): string => `fetchCatalogImport_${importId}`;

/** start / cancel / retry-failed-pages / archive / commit / section maps: one "this import is busy" flag. */
export const catalogImportActionKey = (importId: string): string => `catalogImportAction_${importId}`;

/** Cache key of one page: `${importId}:${pageNo}`. */
export const catalogPageCacheKey = (importId: string, pageNo: number): string => `${importId}:${pageNo}`;
export const catalogPageKey = (importId: string, pageNo: number): string =>
  `fetchCatalogPage_${catalogPageCacheKey(importId, pageNo)}`;
/** Rerun of one page. */
export const catalogRerunKey = (importId: string, pageNo: number): string =>
  `rerunCatalogPage_${catalogPageCacheKey(importId, pageNo)}`;
/** Manual crop on one page. */
export const catalogCropKey = (importId: string, pageNo: number): string =>
  `createCatalogCrop_${catalogPageCacheKey(importId, pageNo)}`;

/** Key of one draft list query (status / flag / page filters). */
export const catalogDraftListCacheKey = (
  importId: string,
  status?: string,
  flag?: string,
  pageNo?: number
): string => `${importId}|${status ?? ""}|${flag ?? ""}|${pageNo ?? ""}`;
export const catalogDraftListKey = (importId: string): string => `fetchCatalogDrafts_${importId}`;

/** PATCH of one draft. */
export const catalogDraftKey = (draftId: string): string => `catalogDraft_${draftId}`;
/** Bulk approve / reject / restore / unapprove on an import. */
export const catalogBulkActionKey = (importId: string): string => `catalogBulkAction_${importId}`;
/** Merge / split on an import. */
export const catalogMergeKey = (importId: string): string => `catalogMergeSplit_${importId}`;
